/* eslint-disable react/jsx-props-no-spreading */
import {isUndefined} from 'lodash';
import React, {FC} from 'react';

import {useToggle} from '../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {findTimeZone} from '../../../../helpers/dateTimeHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsHeatMapMetricsEnum,
  AnalyticsHeatMapMetricValue,
} from '../../../../models/api/analyticsMetricsModel';
import {getAnalyticsSelectedNamespaceTeamBusinessHours} from '../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {
  BusinessHourLimits,
  computeLimitsFromBusinessHours,
  computeLimitsFromDefaultBusinessHours,
} from '../../businessHours/helpers/businessHourLimits';
import {ReportElementProps, ReportElementWrapperProps} from '../reportElement';
import {ExpandedHeatmapDialog} from './expandedHeatmapDialog';
import {computeIsBusinessHoursCompatibleWithUserTimezone, convertTime} from './heatmapTimeHelpers';
import {ReportHeatMapElement} from './reportHeatmapElement';

/*
 * Props.
 */

export interface ReportHeatMapProps
  extends ReportElementWrapperProps,
    ReportElementProps<AnalyticsHeatMapMetricsEnum> {}

/**
 * Component.
 * Wrapper to handle the expanded state.
 */

// @TODO: Implement events once #4951 is merged.
export const ReportHeatMap: FC<ReportHeatMapProps> = (props) => {
  const [isHeatmapExpanded, toggleHeatmapExpanded] = useToggle();

  const businessHours = useAppSelector(getAnalyticsSelectedNamespaceTeamBusinessHours);

  // Compute the business hour limits based on the current timezone and check if they
  // are compatible with the users current timezone
  const potentialBusinessHourLimits = businessHours && computeLimitsFromBusinessHours(businessHours);
  const currentTimeZone = findTimeZone();
  const userTimeZoneCompatibleWithBusinessHours =
    potentialBusinessHourLimits &&
    computeIsBusinessHoursCompatibleWithUserTimezone(potentialBusinessHourLimits, currentTimeZone);

  // If we have business hour limits and the users timezone is compatible with them, use
  // those limits. Otherwise, fall back on the defaults (9-5, 7 days a week)
  const businessHourLimits =
    !isUndefined(potentialBusinessHourLimits) && userTimeZoneCompatibleWithBusinessHours
      ? potentialBusinessHourLimits
      : computeLimitsFromDefaultBusinessHours();

  return (
    <>
      <ReportHeatMapElement
        {...props}
        isHeatmapExpanded={false}
        onToggleHeatmapExpanded={toggleHeatmapExpanded}
        filterValue={(value: AnalyticsHeatMapMetricValue) =>
          filterHeatMapValueOutsideBusinessHours(value, businessHourLimits, currentTimeZone)
        }
        businessHourLimits={businessHourLimits}
      />
      {isHeatmapExpanded && (
        <ExpandedHeatmapDialog {...props} toggleHeatmapExpanded={toggleHeatmapExpanded} />
      )}
    </>
  );
};

/*
 * Helpers.
 */

// Report Heatmap only displays business hours data.
const filterHeatMapValueOutsideBusinessHours = (
  {hour, day}: AnalyticsHeatMapMetricValue,
  businessHourLimits: BusinessHourLimits,
  currentTimeZone: string,
) => {
  if (isUndefined(businessHourLimits.timezone)) {
    return (
      businessHourLimits.days.includes(day) &&
      hour >= businessHourLimits.opensHour &&
      hour <= businessHourLimits.closesHour
    );
  }

  const {day: convertedDay, hour: convertedHour} = convertTime(
    hour,
    day,
    currentTimeZone,
    businessHourLimits.timezone,
  );

  return (
    businessHourLimits.days.includes(convertedDay) &&
    convertedHour >= businessHourLimits.opensHour &&
    convertedHour <= businessHourLimits.closesHour
  );
};
